import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import Artwork from '../components/Artwork.jsx'
import Avatar from '../components/Avatar.jsx'
import CommentThread from '../components/CommentThread.jsx'
import ErrorState, { LoadingState } from '../components/ErrorState.jsx'
import Field from '../components/Field.jsx'
import InlineAlert from '../components/InlineAlert.jsx'
import { ArrowMark, CloseMark } from '../components/Marks.jsx'
import { ApiError, api, is404 } from '../lib/api.js'

/**
 * `/lists/:id` — one list (LIST-001, PLAN §7).
 *
 * Reordering is up/down buttons rather than drag and drop. Positions are
 * stable and the whole order is sent, so both gestures produce the same
 * request; buttons work with a keyboard and on a phone, and drag and drop
 * needs a library, a touch story and a screen-reader story before it works
 * for everybody. That is 1A-5's a11y budget, not this pass's.
 *
 * A list nobody may see is a 404, not a 403 — a 403 confirms it exists.
 */
function ItemRow({ item, index, total, editable, error, reordering, onMove, onRemove }) {
  const media = item.media
  const path = media
    ? `/${media.kind === 'song' ? 'song' : media.kind === 'artist' ? 'artist' : 'album'}/${media.public_id}`
    : null

  return (
    <li className="list-item">
      <span className="list-position" aria-hidden="true">
        {index + 1}
      </span>
      {media && path ? (
        <Link to={path} className="rated-media">
          <Artwork
            art={media.artwork_url ? { url: media.artwork_url } : null}
            title={media.title}
            artist={media.credit_name}
            size={44}
          />
          <span className="row-main">
            <span className="rated-title">{media.title}</span>
            <span className="footnote">
              {media.credit_name}
              {item.note ? ` · ${item.note}` : ''}
            </span>
          </span>
        </Link>
      ) : (
        <span className="row-main footnote">No longer in the catalog</span>
      )}
      {editable ? (
        <span className="list-item-actions">
          <button
            type="button"
            className="icon-btn"
            onClick={() => onMove(index, index - 1)}
            disabled={index === 0 || reordering}
            aria-label={`Move ${media?.title || 'item'} up`}
          >
            <ArrowMark direction="up" />
          </button>
          <button
            type="button"
            className="icon-btn"
            onClick={() => onMove(index, index + 1)}
            disabled={index === total - 1 || reordering}
            aria-label={`Move ${media?.title || 'item'} down`}
          >
            <ArrowMark direction="down" />
          </button>
          <button
            type="button"
            className="icon-btn"
            onClick={() => onRemove(item)}
            aria-label={`Remove ${media?.title || 'item'}`}
          >
            <CloseMark />
          </button>
        </span>
      ) : null}
      {/* `.list-item` is a flex row, so the stylesheet gives this line its own
          basis — the sentence belongs under the row it happened on. */}
      <InlineAlert>{error}</InlineAlert>
    </li>
  )
}

/**
 * The order a refused reorder goes back to: the relative order captured at the
 * press, with membership taken from what the page holds now. A row that was
 * removed while the request was out does not come back with the revert, and
 * one that arrived does not leave with it. Arrivals go last — the captured
 * order says nothing about where they belong, and the next load takes the
 * server's order anyway.
 *
 * Only the *order* comes from the capture. Every row is the copy the page
 * holds now, so a field the server refreshed inside the window is not undone
 * by the revert either.
 */
function revertedOrder(before, current) {
  const byId = new Map(current.map((item) => [item.id, item]))
  const kept = before.filter((item) => byId.has(item.id)).map((item) => byId.get(item.id))
  const keptIds = new Set(kept.map((item) => item.id))
  return [...kept, ...current.filter((item) => !keptIds.has(item.id))]
}

export default function ListDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [row, setRow] = useState(null)
  // The error itself, not a sentence about it: the render needs the status to
  // tell a list that is not here from a request that never arrived.
  const [error, setError] = useState(null)
  // One sentence per row, keyed by the item's id, as the queue keeps its own.
  const [rowErrors, setRowErrors] = useState({})
  // The head's two buttons share a slot, so they share a sentence.
  const [headError, setHeadError] = useState('')
  // Every arrow is disabled while a reorder is out, so two presses cannot race
  // and only one request is ever in flight. That is all it does: `Remove` is
  // not an arrow, and nothing gates an arrow on a removal, so the captured
  // order can still go stale in either direction. `revertedOrder` is what
  // makes the revert exact.
  const [reordering, setReordering] = useState(false)
  // Rename (Q19): title only. The form opens under the head's buttons and the
  // <h1> stays where it is, so the page never has two.
  const [renaming, setRenaming] = useState(false)
  const [draft, setDraft] = useState('')
  const [renameBusy, setRenameBusy] = useState(false)
  const [renameError, setRenameError] = useState('')
  const renameButton = useRef(null)
  const wasRenaming = useRef(false)
  // Delete asks first (QL2): a list goes with every comment on it, other
  // people's included. While it asks, only the pair renders — *Rename* and the
  // visibility button go too — so nothing that deletes sits where the first
  // press landed, whether the buttons are a right-aligned column or, at phone
  // width, a left-aligned row. The rule is `/import`'s; the busy state is
  // Settings' wipe pair's, so a double press sends one request.
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [deleteBusy, setDeleteBusy] = useState(false)
  const deleteButton = useRef(null)
  const keepButton = useRef(null)
  const wasConfirmingDelete = useRef(false)
  const deleteNoteId = useId()

  const load = useCallback(
    async (signal) => {
      setError(null)
      try {
        setRow(await api.list(id, { signal }))
      } catch (err) {
        if (err.name === 'AbortError') return
        setError(err)
      }
    },
    [id],
  )

  useEffect(() => {
    const controller = new AbortController()
    load(controller.signal)
    return () => controller.abort()
  }, [load])

  // Closing the form, saved or cancelled, hands focus back to *Rename*.
  useEffect(() => {
    if (renaming) wasRenaming.current = true
    else if (wasRenaming.current) renameButton.current?.focus()
  }, [renaming])

  // Asking moves focus to *Keep*, the answer that loses nothing; *Keep* hands
  // it back to *Delete*.
  useEffect(() => {
    if (confirmingDelete) {
      wasConfirmingDelete.current = true
      keepButton.current?.focus()
    } else if (wasConfirmingDelete.current) deleteButton.current?.focus()
  }, [confirmingDelete])

  useEffect(() => {
    if (row) document.title = `${row.title} — Sonologue`
    else if (is404(error)) document.title = 'Not found — Sonologue'
  }, [row, error])

  async function move(from, to) {
    const before = row.items
    const order = [...before]
    const [moved] = order.splice(from, 1)
    order.splice(to, 0, moved)
    setRowErrors((current) => ({ ...current, [moved.id]: '' }))
    setReordering(true)
    // The order moves before the answer — a reorder that waits for a round
    // trip feels broken — which is why the refusal has to put it back.
    setRow({ ...row, items: order })
    try {
      setRow(await api.reorderList(id, order.map((item) => item.id)))
    } catch (err) {
      // `items` alone: anything else on the row may have refreshed since. And
      // the captured order is a claim about order, not about membership —
      // whatever left or arrived while this was out stays that way.
      setRow((current) => ({ ...current, items: revertedOrder(before, current.items) }))
      setRowErrors((current) => ({
        ...current,
        [moved.id]: err instanceof ApiError ? err.message : 'Could not save that just now.',
      }))
    } finally {
      setReordering(false)
    }
  }

  async function remove(item) {
    setRowErrors((current) => ({ ...current, [item.id]: '' }))
    try {
      setRow(await api.removeListItem(id, item.id))
    } catch (err) {
      setRowErrors((current) => ({
        ...current,
        [item.id]: err instanceof ApiError ? err.message : 'Could not save that just now.',
      }))
    }
  }

  async function setVisibility(visibility) {
    setHeadError('')
    try {
      setRow(await api.updateList(id, { visibility }))
    } catch (err) {
      // The label reads from `row.visibility`, which did not move, so it is
      // still telling the truth about what pressing it would do.
      setHeadError(err instanceof ApiError ? err.message : 'Could not save that just now.')
    }
  }

  function startRename() {
    setDraft(row.title)
    setRenameError('')
    setRenaming(true)
  }

  async function rename(event) {
    event.preventDefault()
    const title = draft.trim()
    if (!title || title === row.title || renameBusy) return
    setRenameBusy(true)
    setRenameError('')
    try {
      // The response is the row, so the heading and the tab title move with it.
      setRow(await api.updateList(id, { title }))
      setRenaming(false)
    } catch (err) {
      // The form stays open with what was typed; the heading never moved.
      setRenameError(err instanceof ApiError ? err.message : 'Could not save that just now.')
    } finally {
      setRenameBusy(false)
    }
  }

  function askDelete() {
    setRenaming(false)
    setConfirmingDelete(true)
  }

  async function destroy() {
    setHeadError('')
    setDeleteBusy(true)
    try {
      await api.deleteList(id)
      navigate('/')
    } catch (err) {
      // No `navigate('/')`: the list is still here, so the page stays here —
      // and still asking, because closing the pair would read as *Keep*.
      setHeadError(err instanceof ApiError ? err.message : 'That did not delete.')
      setDeleteBusy(false)
    }
  }

  if (error) {
    // Private and deleted are the two things a 404 can mean, and neither of
    // them is what a failed request means.
    return is404(error) ? (
      <div className="card card-pad">
        <div className="empty-state">
          <h1>That list is not here.</h1>
          <p>It may be private, or it may have been deleted.</p>
          <Link className="btn btn-tonal" to="/">
            Back to your feed
          </Link>
        </div>
      </div>
    ) : (
      <ErrorState title="The list did not load" onRetry={() => load()} level={1} />
    )
  }
  if (!row) return <LoadingState />

  return (
    <>
      <div className={`card card-pad fade-up list-head${renaming ? ' renaming' : ''}`}>
        <div className="row-main">
          <h1 className="title-2 mb-0">{row.title}</h1>
          {row.author ? (
            <Link to={`/profile/${row.author.username}`} className="list-author">
              <Avatar name={row.author.display_name || row.author.username} size={24} />
              <span className="footnote">
                {row.author.display_name || row.author.username}
              </span>
            </Link>
          ) : null}
          {row.description ? <p className="mt-2 mb-0">{row.description}</p> : null}
          <p className="footnote mt-2 mb-0">
            {row.item_count} {row.item_count === 1 ? 'entry' : 'entries'}
            {row.visibility === 'private' ? ' · private' : ''}
          </p>
        </div>
        {row.is_mine ? (
          <div className="profile-actions">
            {confirmingDelete ? (
              <>
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={destroy}
                  disabled={deleteBusy}
                  aria-describedby={deleteNoteId}
                >
                  {deleteBusy ? 'Deleting…' : 'Delete this list'}
                </button>
                <button
                  ref={keepButton}
                  type="button"
                  className="btn btn-quiet btn-sm"
                  onClick={() => setConfirmingDelete(false)}
                  disabled={deleteBusy}
                >
                  Keep
                </button>
                <p id={deleteNoteId} className="footnote mb-0">
                  This also removes every comment on it, including other people’s.
                </p>
              </>
            ) : (
              <>
                <button
                  ref={renameButton}
                  type="button"
                  className="btn btn-quiet btn-sm"
                  onClick={startRename}
                  aria-expanded={renaming}
                >
                  Rename
                </button>
                <button
                  type="button"
                  className="btn btn-quiet btn-sm"
                  onClick={() => setVisibility(row.visibility === 'public' ? 'private' : 'public')}
                >
                  Make {row.visibility === 'public' ? 'private' : 'public'}
                </button>
                <button
                  ref={deleteButton}
                  type="button"
                  className="btn btn-quiet btn-sm"
                  onClick={askDelete}
                >
                  Delete
                </button>
              </>
            )}
            <InlineAlert>{headError}</InlineAlert>
          </div>
        ) : null}
        {row.is_mine && renaming ? (
          <form className="list-form" onSubmit={rename}>
            <Field
              label="Title"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              maxLength={140}
              readOnly={renameBusy}
              autoFocus
            />
            <InlineAlert>{renameError}</InlineAlert>
            <div className="list-form-actions">
              <button
                type="submit"
                className="btn btn-primary btn-sm"
                disabled={renameBusy || !draft.trim() || draft.trim() === row.title}
              >
                {renameBusy ? 'Saving…' : 'Save'}
              </button>
              <button
                type="button"
                className="btn btn-quiet btn-sm"
                onClick={() => setRenaming(false)}
                disabled={renameBusy}
              >
                Cancel
              </button>
            </div>
          </form>
        ) : null}
      </div>

      <div className="card">
        {row.items.length ? (
          <ul className="list-items">
            {row.items.map((item, index) => (
              <ItemRow
                key={item.id}
                item={item}
                index={index}
                total={row.items.length}
                editable={row.is_mine}
                error={rowErrors[item.id]}
                reordering={reordering}
                onMove={move}
                onRemove={remove}
              />
            ))}
          </ul>
        ) : (
          <div className="empty-state">
            <h2>Nothing in it yet</h2>
            <p>
              {row.is_mine
                ? 'Add albums from any album page — the "Add to a list" button is under the score.'
                : 'This list is empty.'}
            </p>
          </div>
        )}
      </div>

      <div className="card card-pad">
        <h2 className="title-3">Comments</h2>
        <CommentThread
          kind="lists"
          publicId={row.public_id}
          comments={row.comments}
          onChange={() => load()}
        />
      </div>
    </>
  )
}
